import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CloudRain, ArrowLeft, Key, Copy, Trash, Plus, ArrowSquareOut, Clock, ChartBar, Rocket, Check, X } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const PLAN_FEATURES = {
  free: { color: 'slate', features: ['50 requests/day', 'Weather + Bulk endpoints', 'No PDF reports'] },
  starter: { color: 'blue', features: ['1,000 requests/day', 'Weather + Bulk endpoints', 'No PDF reports'] },
  pro: { color: 'violet', features: ['5,000 requests/day', 'All endpoints including /report', 'PDF report generation'] },
  business: { color: 'amber', features: ['10,000+ requests/day', 'Full access + /report', 'Priority processing'] },
};

const ApiDashboard = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [upgrading, setUpgrading] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [keysRes, usageRes, subRes, plansRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/keys/list`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/keys/usage`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/keys/subscription`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/v1/plans`),
      ]);
      setKeys(keysRes.data);
      setUsage(usageRes.data);
      setSubscription(subRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      toast.error('Failed to load API data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle subscription return
  useEffect(() => {
    const subStatus = searchParams.get('subscription');
    const sessionId = searchParams.get('session_id');
    if (subStatus === 'success' && sessionId) {
      axios.post(`${BACKEND_URL}/api/keys/subscription/activate?session_id=${sessionId}`, {}, { withCredentials: true })
        .then(() => { toast.success('Subscription activated!'); fetchData(); })
        .catch(() => { toast.error('Could not activate subscription. Please contact support.'); });
    }
  }, [searchParams, fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/keys/generate`, {}, { withCredentials: true });
      setNewKey(data.api_key);
      toast.success('API key generated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (prefix) => {
    if (!window.confirm(`Revoke API key ${prefix}...? This cannot be undone.`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/keys/revoke?prefix=${prefix}`, {}, { withCredentials: true });
      toast.success('API key revoked');
      setNewKey(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke key');
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(planId);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/keys/subscription/checkout?plan_id=${planId}&origin_url=${window.location.origin}`,
        {},
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setUpgrading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You will be downgraded to the Free plan.')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/keys/subscription/cancel`, {}, { withCredentials: true });
      toast.success('Subscription canceled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const currentPlanId = subscription?.plan_id || 'free';
  const usagePercent = usage ? Math.min((usage.today / usage.daily_limit) * 100, 100) : 0;

  return (
    <div className="min-h-screen bg-white mobile-nav-padding">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <CloudRain size={28} weight="bold" className="text-blue-600" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
            <div className="flex items-center gap-3 sm:gap-4">
              <Link to="/developer/docs" data-testid="api-docs-link" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                <ArrowSquareOut size={14} weight="bold" />
                <span className="hidden sm:inline">API Docs</span>
                <span className="sm:hidden">Docs</span>
              </Link>
              <Link to="/dashboard" data-testid="back-to-dashboard-link" className="text-sm text-slate-600 hover:text-slate-950 transition-colors flex items-center gap-1.5">
                <ArrowLeft size={16} weight="bold" />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tighter text-slate-950" style={{ fontFamily: 'Chivo' }}>API Access</h1>
          <p className="text-sm text-slate-600 mt-2">Manage your API keys, monitor usage, and upgrade your plan</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-10">

            {/* Current Plan + Usage */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-testid="usage-overview">
              <div className="md:col-span-2 bg-slate-950 text-white p-6">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400 mb-2">Current Plan</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Chivo' }} data-testid="current-plan-name">
                  {usage?.plan_name || 'Free'}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  {usage?.plan_price > 0 ? `$${usage.plan_price}/month` : 'No charge'}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {usage?.report_access && <span className="px-2 py-0.5 text-xs bg-emerald-900 text-emerald-300 font-mono">PDF ACCESS</span>}
                  {usage?.priority && <span className="px-2 py-0.5 text-xs bg-amber-900 text-amber-300 font-mono">PRIORITY</span>}
                </div>
                {currentPlanId !== 'free' && subscription?.status === 'active' && (
                  <button onClick={handleCancel} className="mt-4 text-xs text-slate-500 hover:text-red-400 transition-colors underline" data-testid="cancel-subscription-btn">
                    Cancel subscription
                  </button>
                )}
              </div>
              <div className="bg-white p-6 border border-slate-200">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">Today</p>
                <p className="text-3xl font-mono font-bold text-slate-950" data-testid="usage-today">{usage?.today || 0}</p>
                <p className="text-xs text-slate-500 mt-1">of {usage?.daily_limit || 50} limit</p>
                <div className="mt-3 h-1.5 bg-slate-100 overflow-hidden">
                  <div className={`h-full transition-all ${usagePercent > 80 ? 'bg-red-500' : 'bg-blue-600'}`} style={{ width: `${usagePercent}%` }}></div>
                </div>
              </div>
              <div className="bg-white p-6 border border-slate-200">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">This Week</p>
                <p className="text-3xl font-mono font-bold text-slate-950">{usage?.this_week || 0}</p>
              </div>
              <div className="bg-white p-6 border border-slate-200">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">All Time</p>
                <p className="text-3xl font-mono font-bold text-slate-950">{usage?.all_time || 0}</p>
              </div>
            </div>

            {/* Plans Grid */}
            <div data-testid="plans-grid">
              <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {plans.map((plan) => {
                  const isCurrent = plan.id === currentPlanId;
                  const pf = PLAN_FEATURES[plan.id] || PLAN_FEATURES.free;
                  return (
                    <div key={plan.id} className={`p-6 border ${isCurrent ? 'border-blue-600 ring-2 ring-blue-600 ring-offset-1 bg-blue-50' : 'border-slate-200 bg-white'}`} data-testid={`plan-card-${plan.id}`}>
                      <p className="text-sm font-semibold text-slate-950 mb-1">{plan.name}</p>
                      <p className="text-2xl font-mono font-bold text-slate-950">
                        {plan.price_monthly > 0 ? `$${plan.price_monthly}` : 'Free'}
                        {plan.price_monthly > 0 && <span className="text-sm text-slate-500 font-normal">/mo</span>}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 mb-4">{plan.daily_limit.toLocaleString()} req/day</p>
                      <div className="space-y-2 mb-4">
                        {pf.features.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                            {f.startsWith('No') ? <X size={12} weight="bold" className="text-slate-400" /> : <Check size={12} weight="bold" className="text-emerald-600" />}
                            {f}
                          </div>
                        ))}
                      </div>
                      {isCurrent ? (
                        <span className="block text-center py-2 text-xs font-medium text-blue-700 bg-blue-100 border border-blue-200">Current Plan</span>
                      ) : plan.price_monthly > 0 ? (
                        <button
                          onClick={() => handleUpgrade(plan.id)}
                          disabled={upgrading === plan.id}
                          data-testid={`upgrade-btn-${plan.id}`}
                          className="w-full py-2 text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <Rocket size={14} weight="bold" />
                          {upgrading === plan.id ? 'Redirecting...' : 'Upgrade'}
                        </button>
                      ) : (
                        <span className="block text-center py-2 text-xs text-slate-500">Default plan</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* New Key Banner */}
            {newKey && (
              <div className="p-6 bg-emerald-50 border border-emerald-200" data-testid="new-key-banner">
                <p className="text-sm font-semibold text-emerald-900 mb-2">Your new API key (copy it now — it won't be shown again):</p>
                <div className="flex items-center gap-3">
                  <code className="flex-1 px-4 py-2 bg-white border border-emerald-300 text-sm font-mono text-slate-950 select-all" data-testid="new-key-value">{newKey}</code>
                  <button onClick={() => copyToClipboard(newKey)} data-testid="copy-new-key-btn" className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center gap-1.5">
                    <Copy size={16} weight="bold" />
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* API Keys */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">API Keys</h2>
                <button onClick={handleGenerate} disabled={generating || keys.length >= 3} data-testid="generate-key-btn" className="px-4 py-2 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5">
                  <Plus size={16} weight="bold" />
                  {generating ? 'Generating...' : 'Generate New Key'}
                </button>
              </div>

              {keys.length === 0 ? (
                <div className="text-center py-16 bg-slate-50 border border-slate-200" data-testid="no-keys-notice">
                  <Key size={48} weight="light" className="text-slate-300 mx-auto mb-4" />
                  <p className="text-sm text-slate-500 mb-1">No API keys yet</p>
                  <p className="text-xs text-slate-400">Generate a key to start making API requests</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="keys-list">
                  {keys.map((key) => (
                    <div key={key.prefix} className="flex items-center justify-between p-5 bg-white border border-slate-200" data-testid={`key-row-${key.prefix}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-950 flex items-center justify-center flex-shrink-0">
                          <Key size={20} weight="bold" className="text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-mono text-slate-950">{key.prefix}••••••••••••••••</code>
                            <span className="px-2 py-0.5 text-xs font-mono uppercase bg-slate-100 text-slate-600 border border-slate-200">
                              {currentPlanId}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span>{key.name}</span>
                            <span>Created {key.created_at ? new Date(key.created_at).toLocaleDateString() : 'N/A'}</span>
                            {key.usage && <span>Today: {key.usage.today}/{key.usage.daily_limit}</span>}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => handleRevoke(key.prefix)} data-testid={`revoke-btn-${key.prefix}`} className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors flex items-center gap-1.5">
                        <Trash size={14} weight="bold" />
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            {usage?.recent_requests?.length > 0 && (
              <div data-testid="recent-activity">
                <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Recent Activity</h2>
                <div className="bg-white border border-slate-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Endpoint</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.recent_requests.map((req, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-4 py-2.5 font-mono text-slate-950">{req.endpoint}</td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 text-xs font-mono ${req.success ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                              {req.success ? 'OK' : 'FAIL'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{req.timestamp ? new Date(req.timestamp).toLocaleString() : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ApiDashboard;
